/*
 * @Author: Sergiy Samborskiy 
 * @Date: 2018-03-02 16:53:54 
 * @Last Modified by: Sergiy Samborskiy
 * @Last Modified time: 2018-08-17 13:19:48
 */

import * as React from "react";

import * as XLSX from "xlsx";
import * as _ from "lodash";
import cx from "classnames";
import produce from "immer";

import api from "services";

import Subheader from "material-ui/Subheader";
import {List, ListItem, makeSelectable} from "material-ui/List";
import {Tabs, Tab} from "material-ui/Tabs";
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';

import SpeakerNotesIcon from "material-ui/svg-icons/action/speaker-notes";
import ListIcon from "material-ui/svg-icons/action/list";
import DescriptionIcon from "material-ui/svg-icons/action/description";
import DownloadIcon from "material-ui/svg-icons/file/file-download";
import ReplayIcon from "material-ui/svg-icons/av/replay";

import "./styles.scss";

const SelectableList = makeSelectable(List);

const SKIPPED_CELL = Symbol("skipped-cell");

const USE_CELL_MERGES = false;

export default class PackingListSelector extends React.Component {

    constructor(props, context) {
        super(props, context);

        this.state = {
            tables: [],
            selectedEntry: undefined,
            workbooks: {},
            sheets: {},
            columnTypes: [],
        };

        this.handleReplayRecognition = this.handleReplayRecognition.bind(this);
        this.handleEntrySelectionChange = this.handleEntrySelectionChange.bind(this);
        this.handleLoadSourceFile = this.handleLoadSourceFile.bind(this);
        this.handleRecognizedDataRowClicked = this.handleRecognizedDataRowClicked.bind(this);
        this.handleTabChange = this.handleTabChange.bind(this);
    }

    async componentDidMount() {
        const mailId = this.props.mailId;

        api.loadFieldTypes()
            .then(resp => {
                this.setState({
                    columnTypes: resp.data,
                });
            }).catch(err => {
                console.error("Error while loading field types", err);
            });
        
        try {
            const response = await api.parseMailAttachments(mailId, this.props.attachments);
            const tables = _.flatten(response.data.entries.map(
                entry => entry.tables.map(
                    table => ({
                        ...table,
                        fileName: entry.fileName,
                        logs: table.logs,
                        selectedDataRows: new Array(table.data.length).fill(false)
                    })
                )
            ));

            if (this.props.initialSelection) {
                const { selectedEntry, indexes } = this.props.initialSelection;
                for (const index of  indexes) {
                    tables[selectedEntry].selectedDataRows[index] = true;
                }
            }
    
            this.setState({
                selectedEntry: this.props.initialSelection && this.props.initialSelection.selectedEntry,
                tables
            });
        }
        catch (err) {
            const data = err.response.data || {};
            this.setState({
                error: "Can't recognize attachments",
                errorAdditional: data.error && data.error.stack,
            });
            console.error(`Error getting recognition info`, err);
        }
    }

    async handleLoadSourceFile() {
        const { selectedEntry, tables } = this.state;

        const entry = tables[selectedEntry];

        const workbook = this._getCurrentWorkbook(this.state);
        const sheet = this._getCurrentSheet(workbook);

        if (workbook) {
            if (entry.fileName !== sheet.fileName || entry.meta.sheetIndex !== sheet.sheetIndex) {
                this.setState(produce(state => {
                    const workbook = this._getCurrentWorkbook(state);
                    
                    workbook.sheetIndex = entry.meta.sheetIndex;
                    const sheet = this.prepareSheetData(workbook);

                    workbook.sheets[sheet.sheetIndex] = sheet;
                }));
            }
        }
        else {
            try {
                const data = await api.loadXls(entry.fileName);
                const workbookData = XLSX.read(data, { type: "array" });

                const workbook = {
                    data: workbookData,
                    fileName: entry.fileName,
                    sheetIndex: entry.meta.sheetIndex,
                    sheets: {}
                };

                const sheet = this.prepareSheetData(workbook);

                workbook.sheets[sheet.sheetIndex] = sheet;

                this.setState(produce(state => {
                    state.workbooks[entry.fileName] = workbook;
                }));
            }
            catch(response) {
                this.setState(produce(state => {
                    state.workbooks[entry.fileName] = {
                        data: undefined,
                        fileName: entry.fileName,
                        error: String(response.data),
                    };
                }));
            }
        }

    }

    prepareSheetData(workbook, sheetIndex) {
        const sheet = this._getCurrentSheet(workbook);

        if (sheet) {
            return sheet;
        }

        const wsname = workbook.data.SheetNames[workbook.sheetIndex];
        const ws = workbook.data.Sheets[wsname];
        
        if (!ws) {
            return {
                sheetIndex: workbook.sheetIndex,
                fileName: workbook.fileName,
                error: "There is no sheet found"
            };
        }

        const [data, cols] = prepareData(ws, { maxRows: 100, maxCols: 40 });

        return {
            sheetIndex: workbook.sheetIndex,
            fileName: workbook.fileName,
            data,
            cols,
        };
    }

    handleEntrySelectionChange(event, index) {
        const { tables } = this.state;
        const entry = tables[index];
        if (!entry) {
            return;
        }
        
        this.setState({
            selectedEntry: index,
        }, () => {
            if (this.state.selectedTab === "source-tab") {
                this.handleLoadSourceFile();
            }
        });
    };

    handleRecognizedDataRowClicked(e) {
        e.stopPropagation();

        const shiftKey = e.shiftKey;
        const el = e.target.closest("tr[data-index]");
        const rowIndex = el.dataset.index;

        this.setState(prev => {
            return produce(prev, (state) => {
                const entry = state.tables[state.selectedEntry];

                const row = !(entry.selectedDataRows[rowIndex] || false);
    
                this.fillSelection(
                    (shiftKey && entry.lastSelected !== undefined) ? entry.lastSelected : rowIndex,
                    rowIndex,
                    entry.selectedDataRows,
                    row
                );
                entry.lastSelected = rowIndex;
            });
        })
    }

    async handleDownloadSource(e) {
        const filename = e.currentTarget.dataset.src;

        try {
            const blob = await api.loadXls(filename, true);

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", getFilename(filename));
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        catch (err) {
            console.error(`Error while downloading source file ${filename}`, err)
        }
    }

    async handleReplayRecognition(e) {
        const index = e.currentTarget.dataset.index;

        const table = this.state.tables[index];
        const hints = [];
        if (this.sheet && index == this.state.selectedEntry) {
            const hint = {
                sheet: table.meta.sheetIndex,
                cols: [],
            };

            for (const [col, types] of this.sheet.state.columns) {
                for (const type of types) {
                    hint.cols.push({
                        col,
                        type,
                    })
                }
            }
            
            if (hint.cols.length > 0) {
                hints.push(hint);
            }
        }

        try {
            const attachment = this.props.attachments.find(a => a.s3.path + "/" + a.name == table.fileName);
            const { data } = await api.parseMailAttachments(this.props.mailId, [{
                ...attachment,
                sheets: [table.meta.sheetIndex],
                hints: hints.length > 0 ? hints : undefined,
            }], true);

            this.setState(produce(state => {
                for (const entry of data.entries) {
                    for (const table of entry.tables) {
                        const item = state.tables.find(t => t.fileName == entry.fileName && t.meta.sheetIndex == table.meta.sheetIndex);
                        if (item) {
                            item.data = table.data;
                            item.logs = table.logs;
                            item.meta = table.meta;
                        }
                        else {
                            console.error("Recognized table not found as opened", table);
                        }
                    }
                }
            }));
        }
        catch (err) {
            console.error(`Error while recognizing source file ${table.fileName}`, err)
        }
    }

    handleTabChange(tabValue) {
        this.setState({
            selectedTab: tabValue
        });
    }

    fillSelection(from, to, source, value) {
        let min = parseInt(from);
        let max = parseInt(to);
        if (max < min) {
            [min, max] = [max, min];
        }
        for (let i = min; i <= max; i++) {
            source[i] = value;
        }
    }

    getSelectedData() {
        const { tables, selectedEntry } = this.state;

        const entry = tables[selectedEntry];

        const data = {
            items: [],
            selectedEntry, // data from only one sheet stored now
            indexes: [],
        };
        
        entry.selectedDataRows.forEach((row, index) => {
            if (!row) {
                return;
            }
            data.indexes.push(index);
            data.items.push(entry.data[index]);
        });
        
        return data;
    }

    _getCurrentWorkbook(state) {
        const { tables, selectedEntry, workbooks } = state;
        
        const table = tables[selectedEntry];
        return workbooks[table.fileName];
    }

    _getCurrentSheet(workbook) {
        return workbook && workbook.sheets[workbook.sheetIndex];
    }

    renderRecognizedData(data, header, selections) {
        const { columnTypes } = this.state;
        if (!header.types) {
            return "No header types found";
        }

        const headerTypes = header.types;
        function getFieldProp(type, prop) {
            const field = columnTypes.find(field => field.type === type);
            return field && field[prop];
        }

        return (
            <table className="table table-responsive table-striped">
                <thead>
                    <tr>
                        <th>Use</th>
                        {
                            headerTypes.map(headerProp => <th>{getFieldProp(headerProp.type, "title")}</th>)
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        data.map((item, index) => {
                            return <tr data-index={index} onClick={this.handleRecognizedDataRowClicked} >
                                <td>
                                    <input type="checkbox" checked={selections[index]} onClick={this.handleRecognizedDataRowClicked} />
                                </td>
                                {
                                    // TODO: wait until headerProp.field available to remove temp _.camelCase
                                    headerTypes.map(headerProp => <td>{item[getFieldProp(headerProp.type, "fieldName")]}</td>)
                                }
                            </tr>;
                        })
                    }
                </tbody>
            </table>
        );
    }

    renderTabs() {
        const { tables, selectedEntry, selectedTab } = this.state;

        const table = tables[selectedEntry];

        return (
            <Tabs ref={t => this.tabs = t} value={selectedTab} onChange={this.handleTabChange} className="tabs" contentContainerClassName="tab-container">
                <Tab
                    icon={<ListIcon />}
                    label="Data"
                    value="data-tab"
                >
                    <div className="data-tab">
                        {
                            table && this.renderRecognizedData(table.data, table.meta.header, table.selectedDataRows)
                        }
                    </div>
                </Tab>
                <Tab
                    icon={<DescriptionIcon />}
                    label="Source"
                    value="source-tab"
                    onActive={this.handleLoadSourceFile}
                >
                    { this.renderCurrentSheet() }
                </Tab>
                <Tab
                    icon={<SpeakerNotesIcon />}
                    label="Logs"
                    value="logs-tab"
                >
                    <div className="logs-tab">
                        {
                            table && table.logs.map(log => <Log data={log} />)
                        }
                        <div className="spacer" />
                    </div>
                </Tab>
            </Tabs>
        );
    }

    renderCurrentSheet() {
        const { tables, selectedEntry, selectedTab, columnTypes } = this.state;

        const table = tables[selectedEntry];
        
        const workbook = this._getCurrentWorkbook(this.state);
        if (!workbook) { // selectedTab !== "source-tab"
            return (
                <div>Loading...</div>
            );
        }

        const sheet = this._getCurrentSheet(workbook);

        return (
            <div key={selectedEntry + "@" + workbook.sheetIndex} className="source-tab">
                { workbook.error
                    ? <div>Some Error: {workbook.error}</div>
                    : (sheet.fileName ? <Sheet ref={c => this.sheet = c} file={sheet} header={table.meta.header} columnTypes={columnTypes} /> : null)
                }
            </div>
        );
    }

    renderRecognizedTablesIndex(tables) {
        return tables.map((table, index) => {
                const filename = decodeURIComponent(table.fileName);
                return (
                    <ListItem value={index} className="entry" innerDivStyle={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div className="info">
                            <span className="name">{ table.meta.sheetName || `Table ${index + 1}` }</span>
                            <span className="filename" title={filename}>
                                { getFilename(filename) }
                            </span>
                        </div>
                        <div className="actions">
                            <ReplayIcon className="replay" onClick={this.handleReplayRecognition} data-index={index} />
                            <DownloadIcon className="download" onClick={this.handleDownloadSource} data-src={filename} />
                        </div>
                    </ListItem>
                );
            });
    }

    render() {
        const { error, tables, selectedEntry } = this.state;

        let content = (
            <div className="no-content">Select recognized entry from the left</div>
        );
        if (error) {
            content = (
                <div className="no-content">
                    <h2>{error}</h2>
                    <div style={{ whiteSpace: "pre-wrap", textAlign: "left" }}>{this.state.errorAdditional}</div>
                </div>
            )
        }
        else {
            content = selectedEntry !== undefined && this.renderTabs();
        }


        return (
            <div className="packing-list">
                <div className="col-xs-3">
                    <SelectableList value={selectedEntry} className="entries-list" onChange={this.handleEntrySelectionChange}>
                        <Subheader>Recognized tables</Subheader>
                    {
                        tables.length > 0 && this.renderRecognizedTablesIndex(tables)
                    }
                    </SelectableList>
                </div>
                <div className="col-xs-9">
                    {
                        content
                    }
                </div>
            </div>
        );
    }
}

function Log({ data }) {
    return (
        <div className="log-entry">
            <span className="log-time">{ new Date(data.timestamp).toLocaleTimeString() }: </span>
            { data.scope ? <span className="log-scope">{ data.scope }</span >: null }
            <div className="log-text">{ data.message }</div>

            { data.data ? <div className="log-details">{ renderLogDetails(data.data) }</div> : null }
        </div>
    );
}

function renderLogDetails(data) {
    switch (typeof data) {
        case "string":
            return data;
        default:
            return JSON.stringify(data, null, 2);
    }
}

class Sheet extends React.Component {

    constructor(props, context) {
        super(props, context);

        this.state = {
            columns: this.prepareColumnMap(props.header.types),
            initialColumns: this.prepareColumnMap(props.header.types),
        };

        this.handleColumnTypeChanged = this.handleColumnTypeChanged.bind(this);
    }

    prepareColumnMap(types) {
        const columnMap = new Map();
        for (const type of types || []) {
            for (const col of type.cols) {
                let columnTypes = columnMap.get(col.col);
    
                if (!columnTypes) {
                    columnMap.set(col.col, columnTypes = []);
                }
                columnTypes.push(col.type);
            }
        }
        return columnMap;
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState({
            columns: this.prepareColumnMap(nextProps.header.types),
            initialColumns: this.prepareColumnMap(nextProps.header.types),
        });
    }

    handleColumnTypeChanged(column, values) {
        this.state.columns.set(column, values);
        this.forceUpdate();
    }

    selectionRenderer(values) {
        switch (values.length) {
            case 0:
                return "";
            case 1:
                return values[0];
            default:
                return `${values.length} fields`;
        }
    }

    render() {
        const { file, header } =this.props;
        const { fileName, data, cols, error } = file;
        const { columns, initialColumns } = this.state;

        if (error) {
            return (
                <div>
                    { error }
                </div>
            );
        }
    
        return (
            <React.Fragment>
                { header.types ? null : <h2>No header types found</h2> }
                <table className="table table-responsive">
                    <thead>
                        <tr>
                            <th>#</th>
                        {
                            cols.map(col => {
                                const colTypes = columns.get(col.key);
                                return (
                                    <th key={col.key}>
                                        <div className="with-hint">
                                            {col.name}
                                            <SelectField value={colTypes || []} 
                                                onChange={(_1, _2, values) => this.handleColumnTypeChanged(col.key, values)} 
                                                selectionRenderer={this.selectionRenderer} multiple fullWidth>
                                                {
                                                    this.props.columnTypes.map(c => (
                                                        <MenuItem
                                                            innerDivStyle={{ padding: 0, textAlign: "center", maxWidth: 100 }}
                                                            key={c.type}
                                                            insetChildren={true}
                                                            value={c.type}
                                                            primaryText={c.title}
                                                        />
                                                    ))
                                                }
                                            </SelectField>
                                        </div>
                                    </th>
                                );
                            })
                        }</tr>
                    </thead>
                    <tbody>
                    {
                        data.map((row, index) => {
                            const isHeaderRow = inRowRange(header, index + 1);
                            return <tr key={index} className={cx({ "recognized-header-row": isHeaderRow })}>
                                <td>{index + 1}</td>
                                {
                                    cols.map((col, index) => {
                                        const initialValueColumn = initialColumns.get(index + 1);
                                        
                                        let cell = row[index];
                                        if (cell === SKIPPED_CELL) {
                                            return null;
                                        }
                                        return <td
                                                colSpan={cell && cell.colspan}
                                                rowSpan={cell && cell.rowspan}
                                                key={col.key}
                                                className={cx({ "recognized-value-col": initialValueColumn, "with-tooltip": isHeaderRow && initialValueColumn })}
                                                title={initialValueColumn && buildTextForValueColumn(header.types, initialValueColumn)}
                                                >
                                            { cell && (typeof cell === "object" ? cell.value : cell) }
                                            {/* { isHeaderRow && valueColumn ? <div className="tooltip-content">{ buildTextForValueColumn(valueColumn) }</div> : null } */}
                                        </td>;
                                    })
                                }
                            </tr>;
                        })
                    }
                    </tbody>
                </table>
            </React.Fragment>
        );
    }
}

/**
 * 
 * @param {*} ws 
 * @param {{ maxRows?: number, maxCols?: number }} opts 
 */
function prepareData(ws, opts = {}) {
    const ut = XLSX.utils;
    const range = XLSX.utils.decode_range(ws["!ref"] || "CW101");
    const end = {
        c: Math.min(opts.maxCols || range.e.c,  range.e.c),
        r: Math.min(opts.maxRows || range.e.r,  range.e.r),
    };

    let data = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: true, range: XLSX.utils.encode_range({ c: 0, r: 0 }, end) });

    data = data.slice(0, opts.maxRows || Infinity);
    
    if (USE_CELL_MERGES && ws["!merges"]) {
        for (const merge of ws["!merges"]) {

            const value = data[merge.s.r][merge.s.c];
            
            for (let r = merge.s.r; r <= merge.e.r; r++) {
                for (let c = merge.s.c; c <= merge.e.c; c++) {
                    data[r][c] = SKIPPED_CELL;
                }
            }

            data[merge.s.r][merge.s.c] = { value, colspan: merge.e.c - merge.s.c + 1, rowspan: merge.e.r - merge.s.r + 1 };
        }
    }

    let cols = [];
    let actualMaxCols = 0;
    let lastRowWithCols = 0;
    for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
        if (data[rowIndex].length > actualMaxCols) {
            actualMaxCols = data[rowIndex].length;
        }
        if (data[rowIndex].length > 0) {
            lastRowWithCols = rowIndex;
        }
    }
    if (actualMaxCols > 0) {
        cols = make_cols(XLSX.utils.encode_col(actualMaxCols - 1) + "1", opts.maxCols);
    }
    if (lastRowWithCols < data.length) {
        data = data.slice(0, lastRowWithCols + 1);
    }

    return [data, cols];
}

function getFilename(path) {
    return path.match(/[^\/]+$/i)[0];
}

function inRowRange(header, index) {
    return header.startRow <= index && header.row >= index;
}

function buildTextForValueColumn(types, columns) {
    const cols = _.flatten(types.map(t => t.cols)).filter(c => columns.includes(c.type));
    return cols.map((col, index) => {
        return `Type${index+1}: ${col.type}\nValue${index+1}: ${col.value}`;
    }).join("\n-------------\n");
}

function make_cols(refstr, maxCols = Infinity) {
	let o = [], C = Math.min(XLSX.utils.decode_range(refstr).e.c + 1, maxCols);
	for(var i = 0; i < C; ++i) {
        o[i] = { 
            name: XLSX.utils.encode_col(i), 
            key: i + 1,
        }
    }
	return o;
};


// WEBPACK FOOTER //
// ./app/components/PackingList/PackingListSelector.jsx